import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const CURRENT_ALLOCATION_DISTRIBUTION = 'CURRENT';
export const FULL_PERCENT_TOLERANCE = 0.001;
export const FULL_AMOUNT_TOLERANCE = 0.005;

export function toNumber(value: any): number {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(String(value).replace(',', '.'));
  return isNaN(parsed) ? 0 : parsed;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function roundTo(value: number, digits: number): number {
  return Number(toNumber(value).toFixed(digits));
}

export function maxFractionDigits(maxDigits: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;
    const text = String(raw).replace(',', '.');
    if (!/^\d*\.?\d*$/.test(text)) return { number: true };
    const [, decimals] = text.split('.');
    if (decimals && decimals.length > maxDigits) {
      return { maxFractionDigits: { max: maxDigits } };
    }
    return null;
  };
}

export function allocateAmounts(
  percents: number[],
  totalEuros: number,
): number[] {
  if (!percents.length) return [];
  if (!(totalEuros > 0)) return percents.map(() => 0);

  const totalCents = Math.round(totalEuros * 100);
  const sumPercent = percents.reduce(
    (sum, percent) => sum + toNumber(percent),
    0,
  );

  const exact = percents.map(percent => (totalCents * toNumber(percent)) / 100);
  const rounded = exact.map(value => Math.round(value));

  if (Math.abs(sumPercent - 100) >= FULL_PERCENT_TOLERANCE) {
    return rounded.map(cents => cents / 100);
  }

  const drift = totalCents - rounded.reduce((sum, cents) => sum + cents, 0);
  if (drift === 0) return rounded.map(cents => cents / 100);

  const byRemainder = exact.map((value, index) => ({
    index,
    remainder: value - Math.floor(value),
  }));

  if (drift > 0) {
    byRemainder.sort((a, b) => b.remainder - a.remainder || a.index - b.index);
    for (let i = 0; i < drift && i < byRemainder.length; i++) {
      rounded[byRemainder[i].index] += 1;
    }
  } else {
    byRemainder.sort((a, b) => a.remainder - b.remainder || a.index - b.index);
    for (let i = 0; i < -drift && i < byRemainder.length; i++) {
      rounded[byRemainder[i].index] = Math.max(
        0,
        rounded[byRemainder[i].index] - 1,
      );
    }
  }

  return rounded.map(cents => cents / 100);
}

const PERCENT_UNITS = 1000000;
const PERCENT_SCALE = 10000;

export function allocatePercents(weights: number[]): number[] {
  if (!weights.length) return [];

  const positives = weights.map(weight => Math.max(0, toNumber(weight)));
  const totalWeight = positives.reduce((sum, weight) => sum + weight, 0);
  if (!(totalWeight > 0)) return positives.map(() => 0);

  const exact = positives.map(weight => (PERCENT_UNITS * weight) / totalWeight);
  const units = exact.map(value => Math.floor(value));

  const remaining =
    PERCENT_UNITS - units.reduce((sum, value) => sum + value, 0);

  const byRemainder = exact.map((value, index) => ({
    index,
    remainder: value - Math.floor(value),
  }));
  byRemainder.sort((a, b) => b.remainder - a.remainder || a.index - b.index);

  for (let i = 0; i < remaining && i < byRemainder.length; i++) {
    units[byRemainder[i].index] += 1;
  }

  return units.map(value => value / PERCENT_SCALE);
}
